import React from 'react';
import { LucideIcon, LoaderCircle } from 'lucide-react';
import styles from './Button.module.scss';

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'outline-primary'
    | 'outline-secondary'
    | 'ghost'
    | 'danger'
    | 'link'
    | 'linkText';
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = {
    children?: React.ReactNode;
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: LucideIcon;
    iconPosition?: 'left' | 'right';
    iconOnly?: boolean;
    isLoading?: boolean;
    isRounded?: boolean;
    isIconSpinning?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            variant = 'primary',
            size = 'md',
            icon: Icon,
            iconPosition = 'left',
            iconOnly = false,
            isLoading = false,
            isRounded = false,
            isIconSpinning = false,
            className,
            disabled,
            ...buttonProps
        },
        ref
    ) => {
        const buttonClasses = [
            styles.button,
            styles[variant],
            styles[size],
            isLoading && styles.loading,
            iconOnly && styles.iconOnly,
            iconPosition === 'right' && styles.iconRight,
            isRounded && styles.rounded,
            className,
        ]
            .filter(Boolean)
            .join(' ');

        const iconSize = size === 'sm' ? 16 : size === 'lg' ? 20 : 18;

        return (
            <button ref={ref} className={buttonClasses} disabled={disabled || isLoading} {...buttonProps}>
                {isLoading ? (
                    <LoaderCircle size={iconSize} className={styles.spinner} />
                ) : (
                    <>
                        {Icon && (
                            <Icon
                                size={iconSize}
                                className={`${styles.icon} ${!iconOnly && children ? styles.iconLeft : ''} ${isIconSpinning && styles.spinner}`}
                            />
                        )}

                        {!iconOnly && children && <span className={styles.buttonText}>{children}</span>}
                    </>
                )}
            </button>
        );
    }
);

Button.displayName = 'Button';
